import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["ariaLabel", "children", "disabled", "focused", "hovered", "id", "keyboardFocused", "label", "labelWidth", "loading", "onBlur", "onClick", "onFocus", "onMouseDown", "onMouseOut", "onMouseOver", "onMouseUp", "onPress", "onPressIn", "onPressOut", "onTouchEnd", "onTouchStart", "pressed", "size", "tabIndex", "testID", "title", "UNSAFE_touchableProps"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import React, { cloneElement, Children } from 'react';
import { DesignContextForwardProvider, useDesignContext } from '@klarna/designtokens';
import Loader from '@bubble-ui/molecule-loader';
import Text from '@bubble-ui/primitive-text';
import Touchable from '@bubble-ui/primitive-touchable';
import View from '@bubble-ui/primitive-view';
import { useDesignVersion } from '@bubble-ui/provider-design-version';
import useStyleSheet from '@bubble-ui/hook-use-stylesheet';
import { useInteractiveStateCombined } from '@bubble-ui/hook-use-interactive-state';
import withProvisionalOverrideFromContext from '@bubble-ui/hoc-with-provisional-override-from-context';
import { usePropsSpreadingDeprecationWarning } from '@bubble-ui/hook-use-props-spreading-deprecation-warning';
import { defaultDesignTokens } from './defaultDesignTokens';
import { getIds } from './getIds';
export var baseButtonRound = (displayName, getStyleSheet) => {
  var BaseButtonRoundComponent = _ref => {
    var {
        ariaLabel,
        children,
        disabled: passedDisabledProp,
        focused: passedFocusedProp,
        hovered: passedHoveredProp,
        id,
        keyboardFocused: passedKeyboardFocusedProp,
        label,
        labelWidth,
        loading,
        onBlur,
        onClick,
        onFocus,
        onMouseDown,
        onMouseOut,
        onMouseOver,
        onMouseUp,
        onPress,
        onPressIn,
        onPressOut,
        onTouchEnd,
        onTouchStart,
        pressed: passedPressedProp,
        size = 'regular',
        tabIndex,
        testID,
        title,
        UNSAFE_touchableProps
        /**
         * @deprecated props spreading is outdated and will be removed with v7.0.0
         */
      } = _ref,
      props = _objectWithoutProperties(_ref, _excluded);
    usePropsSpreadingDeprecationWarning({
      componentName: displayName,
      props,
      useInstead: 'UNSAFE_touchableProps',
      removalDate: 'v7.0.0'
    });
    var [interactiveState, eventHandlers] = useInteractiveStateCombined({
      disabled: passedDisabledProp,
      focused: passedFocusedProp,
      hovered: passedHoveredProp,
      keyboardFocused: passedKeyboardFocusedProp,
      onBlur,
      onFocus,
      onMouseDown,
      onMouseOut,
      onMouseOver,
      onMouseUp,
      onPressIn,
      onPressOut,
      onTouchEnd,
      onTouchStart,
      pressed: passedPressedProp
    });
    var {
      disabled,
      focused,
      hovered,
      keyboardFocused: interactiveStateKeyboardFocused,
      pressed
    } = interactiveState;
    var ids = getIds(id);
    var keyboardFocused = !hovered && interactiveStateKeyboardFocused;
    var isSmall = size === 'small';
    var isMedium = size === 'medium';
    var designVersion = useDesignVersion();
    var designContext = useDesignContext(defaultDesignTokens);
    var {
      borderStyle,
      boundaryStyle,
      focusStyle,
      iconStyle,
      iconWrapperStyle,
      loaderProps,
      rootStyle,
      textStyle,
      textWrapperStyle,
      touchableStyle
      // ⚠️ THIS COMPONENT SUPPORTS STYLE SHEET OVERRIDES ⚠️
      // Keep passing all props to the style sheet, even if we don't use them ourselves
    } = useStyleSheet(BaseButtonRoundComponent, getStyleSheet, _objectSpread(_objectSpread({}, props), {}, {
      ariaLabel,
      children,
      designVersion,
      designContext,
      disabled,
      focused,
      hovered,
      id,
      ids,
      isMedium,
      isSmall,
      keyboardFocused,
      label,
      labelWidth,
      loading,
      onClick,
      onPress,
      pressed,
      size,
      tabIndex,
      testID,
      title,
      UNSAFE_touchableProps
    }, eventHandlers));
    return /*#__PURE__*/React.createElement(Touchable, _extends({
      testID: testID
    }, UNSAFE_touchableProps, {
      ariaLabel: ariaLabel || label || title,
      accessibilityState: {
        disabled: disabled || loading
      },
      disabled: disabled || loading,
      title: title || label,
      style: touchableStyle,
      hitSlop: {
        bottom: 5,
        left: 5,
        right: 5,
        top: 5
      },
      onClick: onClick,
      onPress: onPress,
      tabIndex: tabIndex
    }, props, eventHandlers), /*#__PURE__*/React.createElement(View, {
      style: boundaryStyle
    }, /*#__PURE__*/React.createElement(View, {
      style: rootStyle,
      id: ids === null || ids === void 0 ? void 0 : ids.root
    }, /*#__PURE__*/React.createElement(View, {
      style: borderStyle
    }), keyboardFocused && /*#__PURE__*/React.createElement(View, {
      style: focusStyle
    }), /*#__PURE__*/React.createElement(View, {
      style: iconWrapperStyle,
      id: ids === null || ids === void 0 ? void 0 : ids.iconWrapper
    }, /*#__PURE__*/React.createElement(DesignContextForwardProvider, {
      context: designContext
    }, loading ? /*#__PURE__*/React.createElement(Loader, loaderProps) : /*#__PURE__*/cloneElement(Children.only(children), iconStyle)))), label && /*#__PURE__*/React.createElement(View, {
      style: textWrapperStyle
    }, /*#__PURE__*/React.createElement(Text, {
      numberOfLines: 2,
      style: textStyle,
      id: ids === null || ids === void 0 ? void 0 : ids.text
    }, label))));
  };
  BaseButtonRoundComponent.displayName = displayName;
  return withProvisionalOverrideFromContext(BaseButtonRoundComponent);
};
//# sourceMappingURL=baseButtonRound.js.map